"use client";

import { useEffect } from "react";
import { AlertCircle, RefreshCw, Home } from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

/**
 * Locale Error Page
 *
 * This component is shown when an unhandled error occurs in the [locale] route.
 * It's automatically wrapped in an error boundary by Next.js.
 *
 * @see https://nextjs.org/docs/app/building-your-application/routing/error-handling
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log the error to an error reporting service
    console.error("Locale error:", error);

    // In production, send to error tracking service
    // Example: Sentry.captureException(error);
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <CardTitle>Something went wrong</CardTitle>
          </div>
          <CardDescription>
            An unexpected error occurred. Please try refreshing the page or return to the homepage.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {/* Show error details in development */}
          {process.env.NODE_ENV === "development" && (
            <div className="space-y-2">
              <div className="rounded-md bg-muted p-4">
                <p className="font-semibold text-sm">Error Details:</p>
                <p className="mt-1 font-mono text-sm text-muted-foreground">{error.message}</p>
              </div>

              {error.stack && (
                <details className="rounded-md bg-muted p-4">
                  <summary className="cursor-pointer font-semibold text-sm">Stack Trace</summary>
                  <pre className="mt-2 max-h-64 overflow-auto text-xs text-muted-foreground">
                    {error.stack}
                  </pre>
                </details>
              )}

              {error.digest && (
                <p className="text-xs text-muted-foreground">Error ID: {error.digest}</p>
              )}
            </div>
          )}

          {/* Production error message */}
          {process.env.NODE_ENV === "production" && error.digest && (
            <div className="rounded-md bg-muted p-4">
              <p className="text-sm text-muted-foreground">
                If the problem persists, please contact support with this error ID:
              </p>
              <p className="mt-1 font-mono text-sm font-semibold">{error.digest}</p>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex gap-2">
          <Button onClick={reset} className="flex-1">
            <RefreshCw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
          <Button variant="outline" asChild className="flex-1">
            <Link href="/">
              <Home className="mr-2 h-4 w-4" />
              Go Home
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
